import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useMemo, useRef } from 'react';
import classnames from 'classnames';
import { isSSR } from '../../helper';
import { useBeforeMatch } from '../../hooks/useBeforeMatch';
import { AccordionButton } from './AccordionButton';
import { AccordionContext, isAccordionItemSelected } from './AccordionContext';
import { AccordionItemContext } from './AccordionItemContext';
import styles from './AccordionItem.module.scss';
/**
 * `grid-template-rows` interpolation (animation) is supported from:
 * Chrome 107
 * Safari 16.0
 * Firefox 66
 */
const supportsGridTransitions = isSSR || // render modern version on SSR
    // subgrid is supported from Firefox 71 (close enough to 66) & Safari 16.0
    CSS.supports?.('grid-template-columns', 'subgrid') ||
    // form rel attribute has been added to forms in Chrome 107; Safari supports it from 15.4 so we need to check for Safari here too.
    (!navigator?.userAgent?.includes('Version/') && 'rel' in document.createElement('form'));
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
let oldAccordionHelpers;
if (!isSSR && !supportsGridTransitions) {
    oldAccordionHelpers = import('./oldAccordionHelpers');
}
const AccordionItem = ({ activeAccordionTitle, children, className, contentClasses, header, id, isDisabled = false, leftComponent, onClick, onIconClick, subTitle, title, ...rest }) => {
    const contentRef = useRef(null);
    const scrollHeight = useRef(0);
    const firstUpdate = useRef(true);
    const { activeItemId, setActiveItemId } = useContext(AccordionContext);
    const contextValue = useMemo(() => ({
        id,
    }), [id]);
    const isActive = isAccordionItemSelected(activeItemId, id);
    const timeout = useRef();
    useEffect(() => {
        const contentElem = contentRef?.current;
        if (!contentElem)
            return;
        if (firstUpdate.current) {
            if (supportsGridTransitions) {
                if (isActive) {
                    contentElem.firstChild.style.overflow = 'visible';
                }
                else {
                    // @ts-expect-error -- New HTML feature
                    contentElem.hidden = 'until-found';
                }
            }
            else
                (async () => {
                    const helpers = await oldAccordionHelpers;
                    helpers.firstUpdateCall(contentElem, isActive, scrollHeight);
                })();
            firstUpdate.current = false;
            return;
        }
        // clear any current timeout
        clearTimeout(timeout.current);
        if (supportsGridTransitions) {
            contentElem.ontransitionend = null;
            contentElem.firstChild.style.overflow = '';
            contentElem.removeAttribute('hidden');
            const toggleOverflow = () => {
                contentElem.ontransitionend = null;
                if (isActive)
                    contentElem.firstChild.style.overflow = 'visible';
                // @ts-expect-error -- TS doesn't know `until-found` yet
                else
                    contentElem.hidden = 'until-found';
            };
            // fallback in case the animation is not run for various reasons (e.g. "prefers reduced motion" enabled in OS, battery saving turn on, ...)
            timeout.current = setTimeout(toggleOverflow, 420); // animations runs 400 ms, so 420 blaze it
            contentElem.ontransitionend = e => {
                if (e.propertyName === 'grid-template-rows') {
                    clearTimeout(timeout.current);
                    toggleOverflow();
                }
            };
        }
        else {
            // hopefully we can delete this code in 2024+ when we drop support for browsers that don't support grid-template-rows interpolation.
            (async () => {
                const helpers = await oldAccordionHelpers;
                clearTimeout(timeout.current);
                timeout.current = helpers.mainUseEffectCall(contentElem, isActive, scrollHeight);
            })();
        }
        // eslint-disable-next-line consistent-return
        return () => {
            clearTimeout(timeout.current);
            contentElem.ontransitionend = null;
        };
    }, [contentRef, firstUpdate, isActive]);
    useBeforeMatch({
        contentRef,
        onMatch: () => setActiveItemId(id),
    });
    if (!title) {
        return (_jsxs(_Fragment, { children: [header, _jsx("div", { id: id, className: classnames(styles.content, contentClasses), children: children })] }));
    }
    return (_jsxs(AccordionItemContext.Provider, { value: contextValue, children: [header, _jsxs("div", { className: classnames(styles.accordionItem, className, 'mb-0-5x pb-1-5x'), ...rest, children: [_jsxs("div", { className: "d-flex flex-row gap-1x w-100", children: [leftComponent, _jsxs("div", { className: classnames({
                                    [styles.isActive]: isActive,
                                }, 'd-flex flex-column w-100'), children: [_jsx(AccordionButton, { title: isActive && activeAccordionTitle ? activeAccordionTitle : title, onClick: onClick, onIconClick: onIconClick, isDisabled: isDisabled }), subTitle] })] }), _jsx("div", { id: id, className: classnames(styles.content, contentClasses, supportsGridTransitions ? 'd-grid' : undefined), ref: contentRef, "aria-hidden": !isActive, 
                        // @ts-expect-error -- New HTML feature
                        hidden: isActive ? false : 'until-found', children: _jsx("div", { className: supportsGridTransitions
                                ? styles.overflow
                                : classnames(styles.inner, isActive ? 'opacity-100' : 'opacity-0'), children: children }) })] })] }));
};
export { AccordionItem };
